import React, { useState } from 'react';
import { TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { writePersonalNameAndAvatar } from "../helpers/dbFunctions";
import AlertDialog from "./common/AlertDialog";
import PropTypes from "prop-types";
import profanityChecker from "./helpers/profanityChecker";
import { Flex } from "reflexbox";
import Typography from "@mui/material/Typography";
import emojiOptions from "../helpers/emojiOptions";

const colorOptions = [
  "#80CBC4",
  "#E0F2F1",
  "#D32F2F",
  "#FFA000",
  "#1976D2",
  "#388E3C"
];

export const PersonalProfileForm = ({
  name,
  color,
  character,
  onNameChange,
  onColorChange,
  onCharacterChange,
  nameError
}) => {
  const theme = useTheme();
  const selectedRing = `3px solid ${theme.palette.text.primary}`;

  return (
  <Flex style={{ flexDirection: 'column' }}>
    <Flex id="name" style={{ flexDirection: 'column' }}>
      <Typography>Your name</Typography>
      <TextField
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
      />
      {nameError && (
        <Typography variant="caption" color="secondary" style={{ paddingTop: '4px' }}>
          This name does not meet our community guidelines. Please use a different name.
        </Typography>
      )}
    </Flex>
    <Typography style={{ marginTop: '16px' }}>Your Character</Typography>
    <Flex id="character selector" style={{ flexDirection: 'row', flexWrap: 'nowrap', overflow: 'auto' }}>
      {emojiOptions.map((emoji, index) => (
        <Flex
          key={index}
          onClick={() => onCharacterChange(index)}
          style={{
            borderRadius: '50%',
            border: character === index ? selectedRing : null,
            width: '50px',
            minWidth: '50px',
            height: '50px',
            margin: '6px',
            justifyContent: 'center',
            alignItems: 'center',
            boxSizing: 'content-box'
          }}>
          <Typography style={{ fontSize: '28px', lineHeight: 1 }}>
            {emoji}
          </Typography>
        </Flex>
      ))}
    </Flex>
    <Typography style={{ marginTop: '8px' }}>Your Color</Typography>
    <Flex id="color selector" style={{ flexDirection: 'row', flexWrap: 'nowrap', overflow: 'auto' }}>
      {colorOptions.map((c, index) => (
        <Flex
          key={index}
          onClick={() => onColorChange(c)}
          style={{
            backgroundColor: c,
            borderRadius: '50%',
            border: color === c ? selectedRing : null,
            width: '50px',
            minWidth: '50px',
            height: '50px',
            margin: '6px',
            boxSizing: 'content-box'
          }}
        />
      ))}
    </Flex>
  </Flex>
  );
};

PersonalProfileForm.propTypes = {
  name: PropTypes.string,
  color: PropTypes.string,
  character: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onNameChange: PropTypes.func.isRequired,
  onColorChange: PropTypes.func.isRequired,
  onCharacterChange: PropTypes.func.isRequired,
  nameError: PropTypes.bool
};

const PersonalProfileDisplayAndEdit = (props) => {
  const user = useSelector((state) => state.user);
  const { uid, name, color, character } = user;
  const [profanityError, setProfanityError] = useState(false);
  const [localName, setLocalName] = useState(name || '');
  const [localColor, setLocalColor] = useState(color || '');
  const [localCharacter, setLocalCharacter] = useState(character || '');

  const handleNameInputChange = (value) => {
    setLocalName(value);
    setProfanityError(profanityChecker(value));
  };

  const handleSaveName = () => {
    writePersonalNameAndAvatar(uid, localName, localColor, localCharacter);
    if (props.onSaved) {
      props.onSaved({ name: localName, color: localColor, character: localCharacter });
    }
  };

  return (
    <AlertDialog
      open={props.open}
      close={props.close}
      title="Your Profile"
      rightButtonText="Save"
      continue={handleSaveName}
      continueDisabled={profanityError}
      body={
        <PersonalProfileForm
          name={localName}
          color={localColor}
          character={localCharacter}
          onNameChange={handleNameInputChange}
          onColorChange={setLocalColor}
          onCharacterChange={setLocalCharacter}
          nameError={profanityError}
        />
      }
    />
  );
};

PersonalProfileDisplayAndEdit.propTypes = {
  open: PropTypes.any.isRequired,
  close: PropTypes.any.isRequired,
  onSaved: PropTypes.func,
};

export default PersonalProfileDisplayAndEdit;